import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useMeterModels } from "@features/mx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Eye, Pencil, Trash, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/ui/pageHeader";
import { PAGE_SIZE } from "@shared/utils/constants";
import { HoverRevealText } from "@shared/components/HoverRevealText";

export function ModelsTab() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: response, isLoading } = useMeterModels({
    remote_utility_id: remoteUtilityId,
    meter_type: "",
    searchData: searchQuery,
    manufacturer: "",
    is_active: "",
    page: currentPage,
    limit: PAGE_SIZE,
  });

  const tableData = React.useMemo(() => {
    if (!response?.results || response.results.length === 0) {
      return [];
    }
    return response.results.map((m: any) => ({
      id: m.id.toString(),
      name: m.meterModel,
      makeCode: m.meterMake,
      manufacturer: m.meterManufacturer?.name,
      resourceType: m.resourceTypeDisplay,
      resourceSubType: m.resourceSubtypeDisplay,
      description: m.description,
      status: m.isActive ? "Active" : "Inactive",
      created: m.createdDate,
    }));
  }, [response]);

  const totalItems = response?.count || 0;

  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
  };

  const columns: TableColumn<any>[] = [
    { key: "name", header: "Name", sortable: true },
    { key: "makeCode", header: "Make Code", sortable: true },
    { key: "manufacturer", header: "Manufacturer", sortable: true },
    { key: "resourceType", header: "Resource Type", sortable: true },
    { key: "resourceSubType", header: "Resource Sub Type", sortable: true },
    {
      key: "description", header: "Description", render: (value) => <HoverRevealText text={value || "—"} />,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (val: string) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${val === "Active"
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-650 border-gray-200"
          }`}>
          {val}
        </span>
      ),
    },
    { key: "created", header: "Created", sortable: true },
  ];

  const actions = [
    {
      label: "View",
      icon: <Eye className="w-4 h-4 text-gray-500" />,
      onClick: (row: any) => navigate(`/mx/settings/models/view/${row.id}`),
    },
    {
      label: "Edit",
      icon: <Pencil className="w-4 h-4 text-gray-500" />,
      onClick: (row: any) => navigate(`/mx/settings/models/edit/${row.id}`),
    },
    {
      label: "Delete",
      icon: <Trash className="w-4 h-4 text-red-500" />,
      onClick: (row: any) => console.log("Delete", row),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Models"
          description="Manage and configure meter models"
        />
        <Button
          onClick={() => navigate("/mx/settings/models/add")}
          className="bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Model
        </Button>
      </div>
      <div className="border border-gray-150 rounded-xl overflow-hidden p-4">
        <GenericTable
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={isLoading}
          actions={actions}
          actionsType="icons"
          searchPlaceholder="Search models..."
          onSearch={(val) => {
            setSearchQuery(val);
            setCurrentPage(1);
          }}
          pagination={pagination}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
}

export default ModelsTab;
